import {
  CommunityDiscordServer,
  CommunityPatch,
  CommunityProfilePatch,
  CommunityRecord,
} from "@bbb/model";
import {
  AvatarUploadUrlRequest,
  AvatarUploadUrlResponse,
  AvatarCompleteRequest,
  CommunityCreationCompleteRequest,
  CommunityCreationInitResponse,
  CommunityPage,
} from "@interfaces";
import { _apiRequest, API_BASE_URL } from "./_apiClients";

function ensureLogoUrl(record: CommunityRecord): CommunityRecord {
  const logo = record.profile?.logo;

  if (logo && !logo.url && logo.storagePath) {
    const safePath = encodeURI(logo.storagePath);
    const url = `${API_BASE_URL}/v1/storage/mock/${safePath}`;
    return {
      ...record,
      profile: {
        ...record.profile,
        logo: {
          ...logo,
          url,
        },
      },
    };
  }

  return record;
}

/**
 * Fetch communities the authenticated user is a member of.
 * @param token
 * @returns
 */
export function fetchMyCommunities(token: string) {
  return _apiRequest<CommunityRecord[]>(`/v1/communities/mine`, {
    token,
  }).then((items) => items.map(ensureLogoUrl));
}

/**
 * All active communities FOR PUBLIC VIEW
 * @param token
 * @param params
 * @returns
 */
export function fetchLiveCommunities(
  token?: string | null,
  params?: { limit?: number; cursor?: string }
) {
  return _apiRequest<CommunityPage>(`/v1/communities`, {
    token: token ?? undefined,
    params,
    authRequired: false,
  }).then((page) => ({
    ...page,
    items: page.items?.map(ensureLogoUrl) ?? [],
  }));
}

/**
 * Update community profile
 * @param token
 * @param communityId
 * @param profile
 * @returns
 */
export function updateCommunityProfile(
  token: string,
  communityId: string,
  profile: CommunityProfilePatch
) {
  return _apiRequest<CommunityRecord>(`/v1/communities/${communityId}`, {
    method: "PATCH",
    token,
    data: profile,
  }).then(ensureLogoUrl);
}

export function transferCommunityOwnership(
  token: string,
  communityId: string,
  payload: CommunityPatch
) {
  return _apiRequest<CommunityRecord>(
    `/v1/communities/${communityId}/transfer-ownership`,
    {
      method: "POST",
      token,
      data: payload,
    }
  ).then(ensureLogoUrl);
}

export function initiateCommunityCreation(
  token: string,
  payload: AvatarUploadUrlRequest
) {
  return _apiRequest<CommunityCreationInitResponse>(
    "/v1/communities/initiate",
    {
      method: "POST",
      token,
      data: payload,
    }
  );
}

export function completeCommunityCreation(
  token: string,
  communityId: string,
  payload: CommunityCreationCompleteRequest
) {
  return _apiRequest<CommunityRecord>(
    `/v1/communities/${communityId}/creation/complete`,
    {
      method: "POST",
      token,
      data: payload,
    }
  ).then(ensureLogoUrl);
}

export function requestCommunityLogoUploadUrl(
  token: string,
  communityId: string,
  payload: AvatarUploadUrlRequest
) {
  return _apiRequest<AvatarUploadUrlResponse>(
    `/v1/communities/${communityId}/logo/upload-url`,
    {
      method: "POST",
      token,
      data: payload,
    }
  );
}

export function completeCommunityLogoUpload(
  token: string,
  communityId: string,
  payload: AvatarCompleteRequest
) {
  return _apiRequest<CommunityRecord>(
    `/v1/communities/${communityId}/logo/complete`,
    {
      method: "POST",
      token,
      data: payload,
    }
  ).then(ensureLogoUrl);
}

export function disconnectCommunityConnectedSocial(
  token: string,
  communityId: string,
  platform: "twitter" | "discord"
) {
  return _apiRequest<CommunityRecord>(
    `/v1/communities/${communityId}/socials/${platform}/disconnect`,
    {
      method: "POST",
      token,
    }
  ).then(ensureLogoUrl);
}

export function getDiscordCommunityServerSelection(
  token: string,
  selectionToken: string,
) {
  return _apiRequest<{
    communityId: string;
    username?: string | null;
    guilds: Array<Omit<CommunityDiscordServer, "connectedByUserId">>;
  }>(`/v1/discord/community-server-selection/${selectionToken}`, {
    token,
  });
}

export function completeDiscordCommunityServerSelection(
  token: string,
  selectionToken: string,
  payload: {
    guildId: string;
    inviteUrl: string;
  },
) {
  return _apiRequest<CommunityRecord>(
    `/v1/discord/community-server-selection/${selectionToken}/connect`,
    {
      method: "POST",
      token,
      data: payload,
    }
  ).then(ensureLogoUrl);
}
